import React, { useState } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { IconButton, Box, FormControl, FormHelperText, InputLabel } from "@mui/material";
import { styled } from "@mui/system";
import {
  DARK_NAVY,
  DARK_NAVY_LIGHT,
  INPUT_BORDER_DEFAULT,
  INPUT_BORDER_FOCUS,
  INPUT_FOCUS_GLOW,
  PROFILE_FONT,
} from "../../constants/layout";

type InputProps = {
  label: string;
  type: string;
  value: any;
  id: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  name: string;
  suffix?: React.ReactNode;
  isPassword?: boolean;
  error?: string;
  touched?: boolean;
  variant?: string;
  [key: string]: any;
};

const StyledInput = styled("input")<{ $hasError?: boolean }>(({ $hasError }) => ({
  width: "100%",
  padding: "12px 14px",
  border: `2px solid ${$hasError ? "#d32f2f" : INPUT_BORDER_DEFAULT}`,
  borderRadius: "10px",
  fontSize: "14px",
  fontFamily: PROFILE_FONT,
  color: DARK_NAVY,
  boxSizing: "border-box",
  paddingRight: "40px",
  backgroundColor: "#ffffff",
  outline: "none",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  "&::placeholder": {
    fontSize: "13px",
    color: "#9d9d9d",
  },
  "&:hover:not(:disabled)": {
    borderColor: $hasError ? "#d32f2f" : "#b8c9dc",
  },
  "&:focus": {
    borderColor: $hasError ? "#d32f2f" : INPUT_BORDER_FOCUS,
    boxShadow: $hasError ? "none" : INPUT_FOCUS_GLOW,
  },
  "&:disabled": {
    backgroundColor: "#f5f5f5",
    cursor: "not-allowed",
  },
}));

const CommonInput = ({
  label,
  type,
  onChange,
  value,
  suffix,
  isPassword,
  name,
  id,
  error,
  touched,
  disabled,
  placeholder,
  ...props
}: InputProps) => {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const hasError = !!(error && touched);

  const togglePasswordVisibility = () => {
    setPasswordVisible((prev) => !prev);
  };

  return (
    <FormControl margin="normal" fullWidth error={hasError} sx={{ mt: 0, mb: 0 }}>
      <InputLabel
        shrink
        htmlFor={id}
        sx={{
          fontFamily: PROFILE_FONT,
          fontSize: 14,
          color: hasError ? "#d32f2f" : DARK_NAVY_LIGHT,
          bgcolor: "#fff",
          px: 0.5,
          ml: 0.5,
        }}
      >
        {label}
      </InputLabel>
      <Box position="relative" display="flex" alignItems="center" sx={{ mt: 2 }}>
        <StyledInput
          type={isPassword && passwordVisible ? "text" : type}
          name={name}
          onChange={onChange}
          value={type === "file" ? undefined : value}
          id={id}
          placeholder={placeholder || label}
          disabled={disabled}
          $hasError={hasError}
          {...props}
        />
        {isPassword && (
          <IconButton
            onClick={togglePasswordVisibility}
            sx={{
              position: "absolute",
              right: "6px",
              top: "50%",
              transform: "translateY(-50%)",
              color: DARK_NAVY_LIGHT,
            }}
          >
            {passwordVisible ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        )}
        {suffix && <Box ml={1}>{suffix}</Box>}
      </Box>
      {hasError && <FormHelperText sx={{ fontFamily: PROFILE_FONT, mx: 0 }}>{error}</FormHelperText>}
    </FormControl>
  );
};

export default CommonInput;
